import React from 'react';
import { Heart, Users, Globe, Award, HandHeart, Sparkles } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import SocialShare from '../components/SocialShare';

const Peace = () => {
  // Authentic images from sunyatee.org - Practitioners in blue kimono meditating
  const peaceGallery = [
    {
      id: 1,
      title: "Sunyatee Practitioners in Blue Kimono",
      description: "Sunya Retreat Session at Bodhgaya 2019 - Practitioners meditating in blue attire",
      image: "https://lh3.googleusercontent.com/QkotA6N3bFjVbnq9AHdWDh52hXLwOavRpI0TkDe90jUEj6-wdzfCjtvc_3CnvY9qykC0dzywvIQ2LaMn8j5sp21GbChcGnRlxpmiJsTxn92aApTx5zDWJrS-rf-auGZskWBBcD9hLA=w2400",
      source: "sunyatee.org"
    },
    {
      id: 2,
      title: "Blue Kimono Meditation Session",
      description: "Practitioners in traditional blue attire during Bodhgaya retreat",
      image: "https://lh3.googleusercontent.com/Qb2OyhZ41k59aEJlIbukWml4Li6SX2n4ZlDz_6-ULNdCbML-SGBBsjc89nIjvgfBxypV7THA_8Txi6ybu5CmbyKE-TWhdwhv5baE01AK-oyXSQLB6Ntw7sFGUnLzFsYYz6vwxvSQsQ=w2400",
      source: "sunyatee.org"
    },
    {
      id: 3,
      title: "Group Sunya Practice",
      description: "Sunyatees in blue kimono practicing together at retreat",
      image: "https://lh3.googleusercontent.com/xQt6HJYcyfTiAEpTyPxIeR1RgwLFRAmlsQNevg6pPohwPmmDyzexlQurzS67cC_DiJEHsUdjX_KL_OYh4tm6bTF0DodpaAZfAX1EoiNB5qSEoJRJrkW0DdSEngyVGw8NEd9T7vS88A=w2400",
      source: "sunyatee.org"
    },
    {
      id: 4,
      title: "Siddipet Retreat Practitioners",
      description: "Sunya Retreat Session at Siddipet 2019 - Blue kimono meditation",
      image: "https://lh3.googleusercontent.com/tDhQ2M_9Qvz6U7KNeQNOnFDTqXgorMh_sj_5KUbu2y9UqX9yZOirddUYlcDSgRaDaYXVMsXgTnt7XZYX_LSTilJW5bF22bACGAFLGVG6Ajye6FZX0kfskH4m8ASE3kjvjoWSkeux8w=w2400",
      source: "sunyatee.org"
    },
    {
      id: 5,
      title: "Peaceful Blue Kimono Meditation",
      description: "Practitioners in serene meditation at Siddipet retreat center",
      image: "https://lh3.googleusercontent.com/G5BnUP1GbAx6DX1_vTSLe4qVnhe4zHhd6Z2wtkxLEw915zlvI0Rkw8IT8itXnzn8LX5JQwq2H9GxssfkxQ0g_oDAYIoe7r2vwah2_RlGW7ollpVgoDl99ldVbe1LJwu7S283kuoTKg=w2400",
      source: "sunyatee.org"
    },
    {
      id: 6,
      title: "Begumpet Meditation Session",
      description: "Sunyata Meditation Session at Begumpet - Practitioners in blue",
      image: "https://lh3.googleusercontent.com/8VovjADCONXaxb4LelyrivSunk8PREuChYuMKm4DWvrdgRhcM1dwdzrYufoePZlX4oHOdMBLPssuQpQFSxHSwZILrm6NKu-_EFvlXZXHJ70Eu2_VlaiVoozxug_9WWrYLmhXTxcnfg=w2400",
      source: "sunyatee.org"
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      <SEOHead 
        title="Peace & Humanitarian Work"
        description="Discover Dr. Lim Siow Jin's commitment to global peace, humanitarian service, and social development. Learn about initiatives through Sunyatee Foundation and DXN's community impact."
        keywords="peace advocacy, humanitarian work, Sunyatee Foundation, Mother Teresa Award, Albert Schweitzer Award, social development, Dr. Lim Siow Jin philanthropy"
      />
      
      {/* Header */}
      <div className="bg-gradient-to-br from-emerald-700 via-emerald-600 to-teal-600 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex justify-center mb-6">
            <div className="w-20 h-20 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center">
              <Heart className="text-white" size={40} />
            </div>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Peace & Humanitarian Work</h1>
          <p className="text-xl text-emerald-100 max-w-3xl mx-auto">
            Committed to global peace, humanitarian service, and social development through compassionate action
          </p>
          <div className="flex justify-center mt-6">
            <SocialShare 
              title="Peace & Humanitarian Work - Dr. Lim Siow Jin"
              description="Committed to global peace, humanitarian service, and social development through compassionate action."
            />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        
        {/* Authentic Gallery Section */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-4 text-center">Peace Through Awareness & Empowerment</h2>
          <p className="text-gray-600 text-center mb-8 max-w-3xl mx-auto">
            Authentic moments from Sunyatee International Foundation retreats and empowerment programs
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {peaceGallery.map((item) => (
              <div key={item.id} className="group relative rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300">
                <img
                  src={item.image}
                  alt={item.title}
                  className="w-full h-56 object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <h3 className="text-white font-bold text-lg">{item.title}</h3>
                    <p className="text-white/80 text-sm">{item.description}</p>
                  </div>
                </div>
                <div className="absolute top-3 right-3 bg-emerald-600/90 text-white text-xs px-2 py-1 rounded-full">
                  Authentic
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Philosophy Section */}
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">Philosophy of Peace</h2>
          <div className="prose max-w-none">
            <p className="text-gray-700 leading-relaxed text-center max-w-3xl mx-auto">
              Dr. Lim's approach to peace is holistic - recognizing that true global harmony comes from individual transformation, economic empowerment, physical health, and spiritual well-being. Peace is not merely the absence of conflict, but the presence of prosperity, wellness, and inner tranquility.
            </p>
          </div>
        </div>

        {/* Peace Advocacy */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Peace Advocacy & Recognition</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl p-6 shadow-md border border-blue-200">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                  <Globe className="text-blue-600" size={24} />
                </div>
                <h3 className="font-bold text-gray-900">Peace Society Worldwide</h3>
              </div>
              <p className="text-sm text-gray-600 mb-2">Honorary Advisor to UN-affiliated global civil organization</p>
              <p className="text-xs text-blue-700 font-semibold">International Peace Leadership</p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-md border border-amber-200">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                  <Award className="text-amber-600" size={24} />
                </div>
                <h3 className="font-bold text-gray-900">Mother Teresa Award</h3>
              </div>
              <p className="text-sm text-gray-600 mb-2">International recognition for humanitarian service</p>
              <p className="text-xs text-amber-700 font-semibold">Humanitarian Excellence</p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-md border border-green-200">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                  <Heart className="text-green-600" size={24} />
                </div>
                <h3 className="font-bold text-gray-900">Albert Schweitzer Award</h3>
              </div>
              <p className="text-sm text-gray-600 mb-2">Positive Life Foundation recognition (2002)</p>
              <p className="text-xs text-green-700 font-semibold">Compassionate Service</p>
            </div>
          </div>
        </div>

        {/* Humanitarian Initiatives */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Humanitarian Initiatives</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl p-8 border border-emerald-200">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-14 h-14 rounded-full bg-emerald-600 flex items-center justify-center">
                  <Users className="text-white" size={28} />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Through Sunyatee Foundation</h3>
              </div>
              <ul className="space-y-3">
                <li className="flex items-start space-x-3">
                  <div className="w-2 h-2 rounded-full bg-emerald-600 mt-2"></div>
                  <span className="text-gray-700">COVID-19 pandemic relief and food distribution</span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-2 h-2 rounded-full bg-emerald-600 mt-2"></div>
                  <span className="text-gray-700">Support for marginalized communities</span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-2 h-2 rounded-full bg-emerald-600 mt-2"></div>
                  <span className="text-gray-700">Women entrepreneurs empowerment programs</span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-2 h-2 rounded-full bg-emerald-600 mt-2"></div>
                  <span className="text-gray-700">Skill centre for specially-abled and rural women</span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-2 h-2 rounded-full bg-emerald-600 mt-2"></div>
                  <span className="text-gray-700">Self-help group formation and support</span>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-8 border border-blue-200">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-14 h-14 rounded-full bg-blue-600 flex items-center justify-center">
                  <HandHeart className="text-white" size={28} />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Through DXN Platform</h3>
              </div>
              <ul className="space-y-3">
                <li className="flex items-start space-x-3">
                  <div className="w-2 h-2 rounded-full bg-blue-600 mt-2"></div>
                  <span className="text-gray-700">Economic empowerment through business opportunities</span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-2 h-2 rounded-full bg-blue-600 mt-2"></div>
                  <span className="text-gray-700">Global employment creation (15-20M distributors)</span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-2 h-2 rounded-full bg-blue-600 mt-2"></div>
                  <span className="text-gray-700">Charity donations to calamity victims</span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-2 h-2 rounded-full bg-blue-600 mt-2"></div>
                  <span className="text-gray-700">Women's economic independence initiatives</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Philosophy of Giving */}
        <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200">
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center">
              <Sparkles className="text-white" size={32} />
            </div>
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">Philosophy of Giving</h2>
          <div className="max-w-3xl mx-auto space-y-4">
            <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-lg p-6 border-l-4 border-emerald-600">
              <p className="text-gray-700 leading-relaxed">
                <span className="font-bold text-emerald-700">Compassionate action without expectation</span> - Giving is not about receiving recognition or rewards, but about genuine care for humanity
              </p>
            </div>
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 border-l-4 border-blue-600">
              <p className="text-gray-700 leading-relaxed">
                <span className="font-bold text-blue-700">Non-discriminatory support</span> - Help reaches all who need it, regardless of background, religion, or nationality
              </p>
            </div>
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-6 border-l-4 border-purple-600">
              <p className="text-gray-700 leading-relaxed">
                <span className="font-bold text-purple-700">Systemic change over temporary relief</span> - Focus on empowerment and sustainable solutions rather than one-time aid
              </p>
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="mt-16 bg-gradient-to-br from-emerald-600 to-teal-600 rounded-2xl p-12 text-white text-center">
          <h2 className="text-3xl font-bold mb-4">Join the Movement for Peace</h2>
          <p className="text-emerald-100 mb-8 max-w-2xl mx-auto">
            Through Sunya practice, economic empowerment, and compassionate action, we can create a more peaceful and prosperous world together
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="/sunya" className="px-8 py-3 bg-white text-emerald-700 rounded-full font-semibold hover:bg-gray-100 transition-all">
              Learn About Sunya
            </a>
            <a href="https://sifworld.com" target="_blank" rel="noopener noreferrer" className="px-8 py-3 bg-emerald-700 text-white rounded-full font-semibold hover:bg-emerald-800 transition-all border border-white/30">
              Visit Sunyatee Foundation
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Peace;
